import { useEffect, useState, useCallback, useMemo } from 'react';

import { useTheme } from '@mui/material';
import { Box } from '@mui/material';

import AppBar from '../../components/appbar/appbar';
import SideBar from '../../components/sidebar/sidebar';
import ActivityArea from '../../components/activityArea/activityArea';
import ChatBot from '../../components/chat-bot/chat-bot';

import drawerWidth from '../../constants/mui.constants';
// const drawerWidth = 240;
import { useLanguage } from '@erpsquad/common/runtime';
import { getToken } from '@/utils';
import { getV1Settings } from '../../api-client/api.manufacturing/api';
import { useLocation } from 'react-router-dom';
import { ModuleSkeleton } from '../../components/loaders/module-skeleton';

// Local storage key for drawer state
const DRAWER_STATE_KEY = '_is_drawer_open';

// Interface for better type safety
export interface ISidebarLayoutProps {
	menu: any[];
	children: React.ReactNode;
	dashboardPath?: string;
}

export const SidebarLayout = (props: ISidebarLayoutProps) => {
	const theme = useTheme();
	const { currentLanguage } = useLanguage();
	const isRtl = useMemo(
		() => currentLanguage?.direction === 'rtl',
		[currentLanguage]
	);
	// Enhanced state management
	const [forcedOpen, setForcedOpen] = useState(true);
	const [hoverOpen, setHoverOpen] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const location = useLocation();
	const curPathName = useMemo(() => location.pathname, [location.pathname])

	// Memoized computed state for actual drawer open status
	const isDrawerOpen = forcedOpen || (!forcedOpen && hoverOpen);

	// Enhanced drawer control functions with better state management
	const handleDrawerOpen = useCallback(() => {
		setForcedOpen(true);
		setHoverOpen(false); // Clear hover state when forcing open
		try {
			window.localStorage.setItem(DRAWER_STATE_KEY, 'true');
		} catch (error) {
			console.warn('Failed to save drawer state to localStorage:', error);
		}
	}, []);

	const handleDrawerClose = useCallback(() => {
		setForcedOpen(false);
		window.localStorage.setItem('_is_drawer_open', 'false');
	}, []);

	const [wipSettingsData, setWipSettingsData] = useState<any>(null);

	const getSettings = useCallback(async () => {
		// getToken returns { "x-token": string; "x-timezone": number }, but API expects string type for "x-timezone"
		const tokenData = getToken();
		const apiParams = {
			'x-token': tokenData['x-token'],
			'x-timezone': String(tokenData['x-timezone'])
		};
		const response = await getV1Settings(apiParams);
		// The "data" property may not exist on GetV1SettingsResponses, so check existence safely
		const data = (response && (response as any)?.data?.settings?.[0]) || null;
		setWipSettingsData(data);
		localStorage.setItem('wip_settings_data', JSON.stringify(data));
	}, []);
	useEffect(() => {
		if(curPathName?.includes('manufacturing')) {
			getSettings();
		}
	}, [getSettings, curPathName]);

	const [manufacturingSubmenu, setManufacturingSubmenu] = useState<any>(
		props.menu
	);

	useEffect(() => {
		if (wipSettingsData && Array.isArray(props?.menu)) {
			const _new = props.menu.filter((item) => {
				if (!wipSettingsData?.enable_production_planning) {
					return item.label !== 'Plans';
				} else {
					return item;
				}
			});
			setManufacturingSubmenu(_new);
		}
	}, [wipSettingsData, props.menu]);
	// const module_type = '';

	const handleMouseEnter = () => {
		if (!forcedOpen) {
			// setOpen(true);
			window.localStorage.setItem(DRAWER_STATE_KEY, 'true');
		}
	};

	const handleMouseLeave = useCallback(() => {
		if (!forcedOpen && isInitialized) {
			setHoverOpen(false);
			window.localStorage.setItem(DRAWER_STATE_KEY, 'false');
		}
	}, [forcedOpen, isInitialized]);

	// Toggle function for programmatic control
	const toggleDrawer = useCallback(() => {
		if (forcedOpen) {
			handleDrawerClose();
		} else {
			handleDrawerOpen();
		}
	}, [forcedOpen, handleDrawerClose, handleDrawerOpen]);

	// Enhanced initialization with error handling
	useEffect(() => {
		const initializeDrawerState = () => {
			try {
				if (typeof window !== 'undefined' && window.localStorage) {
					const savedState = window.localStorage.getItem(DRAWER_STATE_KEY);
					const shouldBeOpen = savedState === 'true';

					setForcedOpen(shouldBeOpen);
					setHoverOpen(false); // Ensure hover state is reset
					setIsInitialized(true);
				} else {
					// Fallback for environments without localStorage
					setForcedOpen(true);
					setIsInitialized(true);
				}
			} catch (error) {
				console.warn('Failed to read drawer state from localStorage:', error);
				// Fallback to default state
				setForcedOpen(true);
				setIsInitialized(true);
			}
		};

		initializeDrawerState();
	}, []);

	// Handle window focus to sync state across tabs
	useEffect(() => {
		const handleWindowFocus = () => {
			try {
				if (typeof window !== 'undefined' && window.localStorage) {
					const savedState = window.localStorage.getItem(DRAWER_STATE_KEY);
					const shouldBeOpen = savedState === 'true';

					if (shouldBeOpen !== forcedOpen) {
						setForcedOpen(shouldBeOpen);
						setHoverOpen(false);
					}
				}
			} catch (error) {
				console.warn('Failed to sync drawer state on window focus:', error);
			}
		};

		window.addEventListener('focus', handleWindowFocus);

		return () => {
			window.removeEventListener('focus', handleWindowFocus);
		};
	}, [forcedOpen]);

	// Prevent rendering until initialization is complete
	if (!isInitialized) {
		return (
			// <Box
			// 	sx={{
			// 		display: 'flex',
			// 		minHeight: '100vh',
			// 		alignItems: 'center',
			// 		justifyContent: 'center'
			// 	}}>
				<ModuleSkeleton />
			/* </Box> */
		);
	}

	return (
		<Box
			sx={{
				display: 'flex',
				minHeight: '100vh',
				direction: isRtl ? 'rtl' : 'ltr'
			}}>
			<AppBar
				open={forcedOpen}
				handleDrawerOpen={handleDrawerOpen}
				handleDrawerClose={handleDrawerClose}
				toggleDrawer={toggleDrawer}
				width={drawerWidth}
				forcedOpen={forcedOpen}
				hoverOpen={hoverOpen}
			/>

			<SideBar
				open={forcedOpen}
				forcedOpen={forcedOpen}
				hoverOpen={hoverOpen}
				handleDrawerClose={handleDrawerClose}
				handleDrawerOpen={handleDrawerOpen}
				toggleDrawer={toggleDrawer}
				width={drawerWidth}
				theme={theme}
				menu={manufacturingSubmenu}
				handleMouseEnter={handleMouseEnter}
				handleMouseLeave={handleMouseLeave}
				isInitialized={isInitialized}
				dashboardPath={props.dashboardPath}
			/>

			<ActivityArea
				isDrawerOpen={isDrawerOpen}
				drawerWidth={drawerWidth}
				forcedOpen={forcedOpen}>
				{props.children}
			</ActivityArea>
			<ChatBot />
		</Box>
	);
};

export default SidebarLayout;
